import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import PortfolioRequestModal from "@/components/forms/PortfolioRequestModal";
import PortfolioLightbox from "@/components/site/PortfolioLightbox";
import BeforeAfterSlider from "@/components/site/BeforeAfterSlider";
import { useAuth } from "@/context/AuthContext";
import { SAMPLES, CATEGORIES, PROPERTY_TYPES } from "@/data/portfolioSamples";

const FEATURED = {
  address: "906 Irving St. NE.",
  city: "Washington, DC 20017",
  levels: 3,
  totalSqft: 1934,
  full: [
    "/portfolio/906-irving-st-ne/page-1.png",
    "/portfolio/906-irving-st-ne/page-2.png",
  ],
};



export default function Portfolio() {
  const [active, setActive] = useState("bw");
  const [propertyType, setPropertyType] = useState("all");
  const [requestOpen, setRequestOpen] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const category = CATEGORIES.find((c) => c.id === active);
  const { user } = useAuth();

  const filteredItems = useMemo(() => {
    const items = SAMPLES[active] || [];
    if (propertyType === "all") return items;
    return items.filter((it) => it.type === propertyType);
  }, [active, propertyType]);

  // Flatten current tab's samples into a gallery for the lightbox.
  // Redraw items are expanded into Before + After pairs.
  const galleryItems = useMemo(() => (
    active === "redraw"
      ? filteredItems.flatMap((it) => [
          { src: it.before, alt: `${it.label} — Before` },
          { src: it.after, alt: `${it.label} — After` },
        ])
      : filteredItems.map((it) => ({ src: it.src, alt: it.label }))
  ), [active, filteredItems]);

  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });

  // Service/style defaults per category, used to pre-fill the request modal.
  const REQUEST_DEFAULTS = {
    bw: { service_type: "2D Floor Plan", style: "Black & White", label: "Black & White" },
    color: { service_type: "2D Floor Plan", style: "Colorized", label: "Colorized" },
    redraw: { service_type: "Renew Existing Floor Plan", style: "Redraw", label: "Redraw" },
  };

  return (
    <main className="min-h-screen bg-[#FAF7F2] text-stone-900" data-testid="portfolio-root">
      <Nav />

      <section className="pt-32 pb-12 lg:pt-40 lg:pb-16 px-6 lg:px-12 bg-bubbly relative overflow-hidden">
        <div className="blob blob-teal w-[420px] h-[420px] -top-32 -left-32" />
        <div className="blob blob-peach w-[380px] h-[380px] top-1/3 -right-24" />

        <div className="relative mx-auto max-w-7xl">
          <Link
            to="/"
            data-testid="portfolio-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-stone-700 hover:text-stone-900 mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> Back home
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="inline-block bg-white border border-stone-200 px-3 py-1.5 rounded-full text-xs font-semibold text-stone-700 mb-5">
                Portfolio · Selected work
              </span>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02]">
                Real homes,<br />
                <span className="relative inline-block">
                  <span className="relative z-10 italic font-medium">real plans.</span>
                  <span className="absolute inset-x-0 bottom-2 h-3 bg-amber-200/80 rounded-full -z-0" />
                </span>
              </h1>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32 px-6 lg:px-12">
        <div className="mx-auto max-w-7xl">
          {/* Category tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="portfolio-tabs">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setActive(c.id)}
                data-testid={`portfolio-tab-${c.id}`}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${
                  active === c.id
                    ? c.activeBtn
                    : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Property type filter */}
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="portfolio-type-filter">
            <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500 mr-1">
              Property type
            </span>
            {PROPERTY_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setPropertyType(t.id)}
                data-testid={`portfolio-type-${t.id}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  propertyType === t.id
                    ? "bg-stone-900 text-white border-stone-900"
                    : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="bg-white border border-stone-200 rounded-[2rem] p-6 lg:p-8 shadow-sm">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div>
                <span className={`inline-block ${category.chip} px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider mb-2`}>
                  {category.label}
                </span>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                  {category.label} · {filteredItems.length} samples
                </h2>
                <p className="text-stone-600 mt-2 max-w-2xl">{category.blurb}</p>
              </div>
              <button
                type="button"
                onClick={() => setRequestOpen(true)}
                data-testid="portfolio-cta-quote"
                className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors"
              >
                Request similar
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {filteredItems.length === 0 ? (
              <div
                className="py-16 text-center text-stone-500 text-sm"
                data-testid="portfolio-empty"
              >
                No {PROPERTY_TYPES.find((t) => t.id === propertyType)?.label.toLowerCase()} samples in this category yet — pick another property type or ask us for a bespoke sample.
              </div>
            ) : active === "redraw" ? (
              <RedrawGrid items={filteredItems} onOpen={openLightbox} />
            ) : (
              <ImageGrid items={filteredItems} category={active} onOpen={openLightbox} />
            )}
          </div>

          <p className="mt-6 text-xs text-stone-500 text-center">
            Drawings sent from across the country — Redraws are done to closest scale possible and are not to be trusted fully without independent verification.
          </p>
        </div>
      </section>

      <Footer />

      <PortfolioRequestModal
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        defaults={REQUEST_DEFAULTS[active]}
        prefill={user ? { name: user.name, email: user.email } : {}}
      />

      <PortfolioLightbox
        open={lightbox.open}
        items={galleryItems}
        index={lightbox.index}
        onIndexChange={(i) => setLightbox({ open: true, index: i })}
        onClose={closeLightbox}
      />
    </main>
  );
}

function ImageGrid({ items, category, onOpen }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((item, idx) => (
        <button
          key={item.src}
          type="button"
          onClick={() => onOpen?.(idx)}
          data-testid={`portfolio-sample-${category}-${idx + 1}`}
          className="text-left group block rounded-2xl overflow-hidden border border-stone-200 bg-[#FAF7F2] hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          <div className="aspect-[4/3] bg-white overflow-hidden">
            <img
              src={item.src}
              alt={item.label}
              loading="lazy"
              className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-500"
            />
          </div>
        </button>
      ))}
    </div>
  );
}

function RedrawGrid({ items, onOpen }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {items.map((item, idx) => (
        <div
          key={item.after}
          className="rounded-2xl overflow-hidden border border-stone-200 bg-[#FAF7F2]"
          data-testid={`portfolio-sample-redraw-${idx + 1}`}
        >
          <BeforeAfterSlider before={item.before} after={item.after} alt={item.label} />
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white border-t border-stone-200">
            <span className="text-xs font-semibold text-stone-700 truncate">
              {item.label}
            </span>
            <button
              type="button"
              onClick={() => onOpen?.(idx * 2 + 1)}
              className="text-[11px] font-semibold text-teal-700 hover:text-stone-900 whitespace-nowrap"
              data-testid={`portfolio-redraw-fullscreen-${idx + 1}`}
            >
              Fullscreen after →
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
